import React, { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import {
    Check,
    ListMusic,
    Maximize2,
    Minimize2,
    Pause,
    Play,
    SkipBack,
    SkipForward,
    Volume2,
    VolumeX,
    X,
} from 'lucide-react'
import { isNativeIOSApp } from '../../utils/nativeApp'
import { panelSwipeExclusionProps } from './panelSwipeExclusions'
import { DEFAULT_VIDEO_PLAYBACK_RATES, normalizePlaybackRate } from 'lib/videoPlaybackRates'
import VideoSettingsMenu from './VideoSettingsMenu'
import { playlistMenuLabel } from './MediaDock/useMediaKeyInPlaylist'
import styles from './video-chrome.module.scss'

const clampRatio = value => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return 0
    return Math.max(0, Math.min(100, numeric))
}

const clampVolume = value => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return 1
    return Math.max(0, Math.min(1, numeric))
}

const HOVER_VOLUME_SLIDER_QUERY = '(hover: hover) and (pointer: fine)'

const canHoverVolumeSlider = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(HOVER_VOLUME_SLIDER_QUERY).matches

const formatVideoTime = seconds => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'

    const totalSeconds = Math.floor(seconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainder = totalSeconds % 60

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    }

    return `${minutes}:${String(remainder).padStart(2, '0')}`
}

const buildChromeStyle = (progressPercent, volume) =>
    /** @type {React.CSSProperties & { '--video-chrome-progress': string, '--video-chrome-volume': string }} */ ({
        '--video-chrome-progress': `${clampRatio(progressPercent)}%`,
        '--video-chrome-volume': `${clampVolume(volume) * 100}%`,
    })

const stopChromeEvent = event => {
    event.stopPropagation()
}

const stopChromeActivationEvent = event => {
    event.preventDefault()
    event.stopPropagation()
}

const chromeActivationBoundaryHandlers = {
    onClick: stopChromeActivationEvent,
    onDoubleClick: stopChromeActivationEvent,
    onAuxClick: stopChromeActivationEvent,
}

const VideoChrome = ({
    variant = 'overlay',
    visible = true,
    isPlaying = false,
    currentTime = 0,
    duration = 0,
    volume = 1,
    playbackRate = 1,
    isFullscreen = false,
    playbackRates = DEFAULT_VIDEO_PLAYBACK_RATES,
    captionsAvailable = false,
    captionsEnabled = false,
    seekDisabled = false,
    volumeDisabled = false,
    playbackRateDisabled = false,
    previousDisabled = false,
    nextDisabled = false,
    onTogglePlayback = undefined,
    onSeek = undefined,
    onSeekStart = undefined,
    onSeekEnd = undefined,
    onVolumeChange = undefined,
    onPlaybackRateChange = undefined,
    onToggleCaptions = undefined,
    onPrevious = undefined,
    onNext = undefined,
    onExpand = undefined,
    onCollapse = undefined,
    onFullscreen = undefined,
    onClose = undefined,
    // Playlist toggle at the right edge, with the count of listeners who have
    // the post in their playlist. Shown only when a handler is given.
    inPlaylist = false,
    playlistCount = 0,
    onTogglePlaylist = undefined,
    onChromeIntentStart = undefined,
    onChromeIntentEnd = undefined,
    className = undefined,
    dataTestId = 'video-chrome',
}) => {
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0
    const safeCurrentTime = Math.min(
        Number.isFinite(currentTime) ? Math.max(0, currentTime) : 0,
        safeDuration || currentTime || 0,
    )
    const safeVolume = clampVolume(volume)
    const safePlaybackRate = normalizePlaybackRate(playbackRate) ?? 1
    const progressPercent = safeDuration > 0 ? (safeCurrentTime / safeDuration) * 100 : 0
    const style = useMemo(
        () => buildChromeStyle(progressPercent, safeVolume),
        [progressPercent, safeVolume],
    )

    const handleSeek = event => {
        const nextTime = Number(event.currentTarget.value)
        if (!Number.isFinite(nextTime)) return
        onSeek?.(nextTime)
    }

    const handleVolume = event => {
        onVolumeChange?.(clampVolume(event.currentTarget.value))
    }

    const isMuted = safeVolume <= 0.001
    const lastAudibleVolumeRef = useRef(1)
    useEffect(() => {
        if (!isMuted) lastAudibleVolumeRef.current = safeVolume
    }, [isMuted, safeVolume])

    const volumeControlRef = useRef(null)
    const [usesHoverVolumeSlider, setUsesHoverVolumeSlider] = useState(canHoverVolumeSlider)
    const [isVolumeSliderOpen, setIsVolumeSliderOpen] = useState(false)
    const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false)

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return undefined

        const mediaQuery = window.matchMedia(HOVER_VOLUME_SLIDER_QUERY)
        const syncHoverVolumeMode = () => {
            setUsesHoverVolumeSlider(mediaQuery.matches)
            setIsVolumeSliderOpen(false)
        }

        syncHoverVolumeMode()
        mediaQuery.addEventListener?.('change', syncHoverVolumeMode)
        return () => mediaQuery.removeEventListener?.('change', syncHoverVolumeMode)
    }, [])

    // Capture, because the chrome stops these events on the way back up.
    useEffect(() => {
        if (!isVolumeSliderOpen || typeof document === 'undefined') return undefined

        const closeOnOutsidePointer = event => {
            if (volumeControlRef.current?.contains(event.target)) return
            setIsVolumeSliderOpen(false)
        }

        document.addEventListener('pointerdown', closeOnOutsidePointer, true)
        return () => document.removeEventListener('pointerdown', closeOnOutsidePointer, true)
    }, [isVolumeSliderOpen])

    const handleVolumeButtonClick = event => {
        event.preventDefault()
        event.stopPropagation()

        // Touch has no hover to open the flyout with, so the button becomes the
        // slider's toggle there; muting stays a drag to the bottom of it.
        if (!usesHoverVolumeSlider) {
            setIsVolumeSliderOpen(open => !open)
            return
        }

        onVolumeChange?.(isMuted ? lastAudibleVolumeRef.current || 1 : 0)
    }

    const blurContainedActiveElement = event => {
        if (typeof document === 'undefined' || typeof HTMLElement === 'undefined') return
        const activeElement = document.activeElement
        if (activeElement instanceof HTMLElement && event.currentTarget.contains(activeElement)) {
            activeElement.blur()
        }
    }

    const canSeek = !seekDisabled && safeDuration > 0
    const showVolume = typeof onVolumeChange === 'function' && !volumeDisabled
    const volumeSliderOpen = showVolume && !usesHoverVolumeSlider && isVolumeSliderOpen
    const expandHandler = onCollapse || onExpand
    const ExpandIcon = onCollapse ? Minimize2 : Maximize2
    const expandLabel = onCollapse ? 'Collapse player' : 'Expand player'
    const FullscreenIcon = isFullscreen ? Minimize2 : Maximize2
    const fullscreenLabel = isFullscreen ? 'Exit fullscreen' : 'Fullscreen'

    return (
        <div
            className={cn(
                styles.chrome,
                variant === 'below' ? styles.chromeBelow : styles.chromeOverlay,
                variant === 'overlay' && isNativeIOSApp() && styles.chromeNativeIOSOverlay,
                visible && styles.chromeVisible,
                volumeSliderOpen && styles.chromeVolumeSliderOpen,
                isSettingsMenuOpen && styles.chromeVolumeSliderOpen,
                className,
            )}
            style={style}
            data-testid={dataTestId}
            {...panelSwipeExclusionProps}
            {...chromeActivationBoundaryHandlers}
            onMouseDown={stopChromeEvent}
            onPointerDown={stopChromeEvent}
            onMouseEnter={onChromeIntentStart}
            onMouseLeave={onChromeIntentEnd}
            onFocusCapture={onChromeIntentStart}
            onBlurCapture={onChromeIntentEnd}
            onTouchStart={stopChromeEvent}
        >
            <input
                type="range"
                min="0"
                max={safeDuration || 0}
                step="0.01"
                value={canSeek ? safeCurrentTime : 0}
                className={styles.seekRange}
                disabled={!canSeek}
                onInput={handleSeek}
                onChange={handleSeek}
                onMouseDown={onSeekStart}
                onMouseUp={onSeekEnd}
                onTouchStart={onSeekStart}
                onTouchEnd={onSeekEnd}
                aria-label="Seek"
            />
            <div className={styles.controlsRow}>
                <button
                    type="button"
                    className={styles.playButton}
                    onClick={onTogglePlayback}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? (
                        <Pause aria-hidden="true" />
                    ) : (
                        <Play className={styles.playIconOffset} aria-hidden="true" />
                    )}
                </button>

                <span className={styles.timeText}>
                    {formatVideoTime(safeCurrentTime)} / {formatVideoTime(safeDuration)}
                </span>

                <span className={styles.spacer} aria-hidden="true" />

                {onPrevious ? (
                    <button
                        type="button"
                        className={styles.iconButton}
                        disabled={previousDisabled}
                        onClick={onPrevious}
                        aria-label="Previous track"
                    >
                        <SkipBack aria-hidden="true" />
                    </button>
                ) : null}

                {onNext ? (
                    <button
                        type="button"
                        className={styles.iconButton}
                        disabled={nextDisabled}
                        onClick={onNext}
                        aria-label="Next track"
                    >
                        <SkipForward aria-hidden="true" />
                    </button>
                ) : null}

                {showVolume ? (
                    <span
                        ref={volumeControlRef}
                        className={styles.volumeControl}
                        onMouseLeave={blurContainedActiveElement}
                        {...chromeActivationBoundaryHandlers}
                        onMouseDown={stopChromeEvent}
                        onPointerDown={stopChromeEvent}
                        onTouchStart={stopChromeEvent}
                        data-volume-slider={volumeSliderOpen ? 'open' : undefined}
                    >
                        <button
                            type="button"
                            className={styles.iconButton}
                            onClick={handleVolumeButtonClick}
                            aria-expanded={usesHoverVolumeSlider ? undefined : volumeSliderOpen}
                            aria-label={
                                usesHoverVolumeSlider ? (isMuted ? 'Unmute' : 'Mute') : 'Volume'
                            }
                        >
                            {isMuted ? (
                                <VolumeX aria-hidden="true" />
                            ) : (
                                <Volume2 aria-hidden="true" />
                            )}
                        </button>
                        <span
                            className={styles.volumePopover}
                            {...chromeActivationBoundaryHandlers}
                            onMouseDown={stopChromeEvent}
                            onPointerDown={stopChromeEvent}
                            onTouchStart={stopChromeEvent}
                        >
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={safeVolume}
                                className={styles.volumeRange}
                                onInput={handleVolume}
                                onChange={handleVolume}
                                aria-label="Volume"
                            />
                        </span>
                    </span>
                ) : null}

                <VideoSettingsMenu
                    className={styles.settingsControl}
                    buttonClassName={styles.iconButton}
                    popoverClassName={styles.settingsPopover}
                    playbackRate={safePlaybackRate}
                    playbackRates={playbackRates}
                    onPlaybackRateChange={playbackRateDisabled ? undefined : onPlaybackRateChange}
                    captionsAvailable={captionsAvailable}
                    captionsEnabled={captionsEnabled}
                    onToggleCaptions={onToggleCaptions}
                    onMouseLeave={blurContainedActiveElement}
                    onOpenChange={setIsSettingsMenuOpen}
                    dataTestId={`${dataTestId}-settings`}
                />

                {expandHandler ? (
                    <button
                        type="button"
                        className={styles.iconButton}
                        onClick={onCollapse || onExpand}
                        aria-label={expandLabel}
                    >
                        <ExpandIcon aria-hidden="true" />
                    </button>
                ) : null}

                {onFullscreen ? (
                    <button
                        type="button"
                        className={styles.iconButton}
                        onClick={onFullscreen}
                        aria-label={fullscreenLabel}
                    >
                        <FullscreenIcon aria-hidden="true" />
                    </button>
                ) : null}

                {onClose ? (
                    <button
                        type="button"
                        className={styles.iconButton}
                        onClick={onClose}
                        aria-label="Close player"
                    >
                        <X aria-hidden="true" />
                    </button>
                ) : null}

                {onTogglePlaylist ? (
                    <button
                        type="button"
                        className={cn(
                            styles.iconButton,
                            styles.playlistButton,
                            inPlaylist && styles.playlistButtonActive,
                        )}
                        onClick={onTogglePlaylist}
                        aria-label={playlistMenuLabel(inPlaylist)}
                        aria-pressed={inPlaylist}
                        title={playlistMenuLabel(inPlaylist)}
                        data-testid={`${dataTestId}-playlist`}
                    >
                        {inPlaylist ? (
                            <Check aria-hidden="true" />
                        ) : (
                            <ListMusic aria-hidden="true" />
                        )}
                        {playlistCount > 0 ? (
                            <span className={styles.playlistCount}>{playlistCount}</span>
                        ) : null}
                    </button>
                ) : null}
            </div>
        </div>
    )
}

export default React.memo(VideoChrome)
